<template>
  <div class="campaign-member-point-ledger">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary"></b-spinner>
      <p class="mt-2 text-muted">กำลังโหลดข้อมูลสมุดแต้ม...</p>
    </div>

    <div v-else>
      <!-- Summary Cards -->
      <b-row class="mb-6">
        <b-col cols="12" md="4" class="mb-4 mb-md-0">
          <div class="card card-custom shadow-none border bg-light-primary card-stretch">
            <div class="card-body p-5">
              <span class="text-muted font-weight-bold font-size-sm d-block mb-1">
                แต้มคงเหลือปัจจุบัน 
              </span>
              <div class="d-flex align-items-baseline">
                <span class="text-primary font-weight-bolder font-size-h2">
                  {{ Number(summary.balance || 0).toLocaleString() }}
                </span>
                <span class="text-muted font-size-sm ml-2">แต้ม</span>
              </div>
              <div class="text-muted font-size-xs mt-1">
                สำหรับนำไปแลกของรางวัล
              </div>
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="4" class="mb-4 mb-md-0">
          <div class="card card-custom shadow-none border bg-light-success card-stretch">
            <div class="card-body p-5">
              <span class="text-muted font-weight-bold font-size-sm d-block mb-1">
                แต้มสะสมตลอดชีพ 
              </span>
              <div class="d-flex align-items-baseline">
                <span class="text-success font-weight-bolder font-size-h2">
                  {{ Number(summary.lifetime_point || 0).toLocaleString() }}
                </span>
                <span class="text-muted font-size-sm ml-2">แต้ม</span>
              </div>
              <div class="text-muted font-size-xs mt-1">
                ใช้สำหรับการประเมินระดับสมาชิก
              </div>
            </div>
          </div>
        </b-col>

        <b-col cols="12" md="4">
          <div class="card card-custom shadow-none border bg-light card-stretch">
            <div class="card-body p-5">
              <span class="text-muted font-weight-bold font-size-sm d-block mb-1">
                ระดับสมาชิกปัจจุบัน 
              </span>
              <div class="d-flex align-items-center mt-1">
                <span
                  class="badge font-weight-bold px-3 py-2 font-size-base"
                  :style="{
                    backgroundColor: (summary.current_tier.color || '#3699FF') + '25',
                    color: summary.current_tier.color || '#3699FF',
                    border: `1px solid ${(summary.current_tier.color || '#3699FF')}50`
                  }"
                >
                  {{ summary.current_tier.name }}
                </span>
                <span
                  v-if="summary.current_tier.multiplier > 1"
                  class="badge badge-light-warning ml-2 font-weight-bolder"
                >
                  x{{ summary.current_tier.multiplier }} แต้ม
                </span>
              </div>
              <div class="text-muted font-size-xs mt-2">
                เกณฑ์แต้ม: {{ Number(summary.current_tier.min_point || 0).toLocaleString() }} แต้มขึ้นไป
              </div>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Ledger Table Card -->
      <div class="card card-custom gutter-b shadow-none border">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center flex-wrap mb-4" style="gap: 12px;">
            <div>
              <h5 class="card-title text-dark mb-1 font-weight-bolder">
                สมุดแต้ม 
              </h5>
              <span class="text-muted font-size-sm">
                บันทึกบัญชีรายการเคลื่อนไหวของแต้มทั้งหมด แยกตามประเภทธุรกรรม
              </span>
            </div>

            <div class="d-flex align-items-center flex-wrap" style="gap: 10px;">
              <b-form-select
                v-model="filterCampaign"
                size="sm"
                :options="campaignFilterOptions"
                style="width: 200px;"
                @change="onFilterChange"
              />

              <b-form-select
                v-model="filterType"
                size="sm"
                :options="typeFilterOptions"
                style="width: 170px;"
                @change="onFilterChange"
              />

              <!-- <b-button
                variant="primary"
                size="sm"
                class="font-weight-bolder px-4"
                @click="openAdjustModal"
              >
                ปรับแต้มมือ 
              </b-button> -->
            </div>
          </div>

          <div class="table-responsive">
            <b-table
              :items="items"
              :fields="fields"
              head-variant="light"
              hover
              show-empty
              empty-text="ไม่พบข้อมูลสมุดแต้ม"
              class="align-middle mb-0 ledger-table"
              @row-clicked="openDetailModal"
            >
              <template #cell(index)="data">
                <span class="text-muted font-size-sm">
                  {{ (page - 1) * perPage + data.index + 1 }}
                </span>
              </template>

              <template #cell(created_at)="data">
                <div class="d-flex flex-column text-nowrap">
                  <span class="font-weight-bold text-dark font-size-sm">
                    {{ formatDateTime(data.item.created_at) }}
                  </span>
                  <span class="text-muted font-size-xs">
                    {{ data.item.reference_code || '—' }}
                  </span>
                </div>
              </template>

              <template #cell(type)="data">
                <span
                  class="label label-inline font-weight-bold text-nowrap"
                  :class="getTypeBadgeClass(data.item.type)"
                >
                  {{ getTypeLabel(data.item.type) }}
                </span>
              </template>

              <template #cell(campaign)="data">
                <span v-if="data.item.campaign" class="font-weight-bold text-dark font-size-sm">
                  {{ data.item.campaign.name }}
                </span>
                <span v-else class="text-muted font-italic font-size-sm">—</span>
              </template>

              <template #cell(points)="data">
                <span
                  class="font-weight-bolder font-size-lg"
                  :class="data.item.earned_point > 0 ? 'text-success' : 'text-danger'"
                >
                  {{ data.item.earned_point > 0 ? '+' : '' }}{{ Number(data.item.earned_point).toLocaleString() }}
                </span>
              </template>

              <template #cell(base_point)="data">
                <template v-if="data.item.base_point != null">
                  <span class="font-weight-bold text-dark font-size-sm">
                    {{ Number(data.item.base_point).toLocaleString() }}
                  </span>
                  <div class="text-muted font-size-xs">
                    {{ data.item.source_type_label || '—' }}
                  </div>
                </template>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(multiplier)="data">
                <span
                  v-if="data.item.point_multiplier != null"
                  class="font-weight-bold text-dark font-size-sm"
                >
                  x{{ data.item.point_multiplier }}
                </span>
                <span v-else class="text-muted">—</span>
              </template>

              <template #cell(balance_after)="data">
                <span class="font-weight-bold text-dark font-size-sm">
                  {{ Number(data.item.balance_after || 0).toLocaleString() }}
                </span>
              </template>

              <template #cell(lifetime_after)="data">
                <span class="font-weight-bold text-muted font-size-sm">
                  {{ Number(data.item.lifetime_after || 0).toLocaleString() }}
                </span>
              </template>

              <template #cell(tier)="data">
                <span
                  v-if="data.item.member_tier"
                  class="label label-inline font-weight-bold"
                  :style="{
                    backgroundColor: (data.item.member_tier.color || '#3699FF') + '20',
                    color: data.item.member_tier.color || '#3699FF',
                    border: `1px solid ${(data.item.member_tier.color || '#3699FF')}40`
                  }"
                >
                  {{ data.item.member_tier.name }}
                </span>
                <span v-else class="text-muted font-italic font-size-sm">—</span>
              </template>

              <template #cell(created_by)="data">
                <span
                  class="d-inline-block text-truncate text-muted font-size-sm created-by-name"
                  :title="data.item.created_by_name || 'ระบบอัตโนมัติ'"
                >
                  {{ data.item.created_by_name || 'ระบบอัตโนมัติ' }}
                </span>
              </template>

              <template #cell(actions)="data">
                <a
                  href="javascript:"
                  class="btn btn-icon btn-light btn-sm"
                  v-b-tooltip.hover
                  title="ดูรายละเอียด"
                  @click.prevent.stop="openDetailModal(data.item)"
                >
                  <span class="svg-icon svg-icon-md svg-icon-primary">
                    <inline-svg src="media/svg/icons/General/Visible.svg"></inline-svg>
                  </span>
                </a>
              </template>
            </b-table>
          </div>

          <div
            v-if="totalRows > 0"
            class="d-flex justify-content-between align-items-center flex-wrap mt-4 pt-2 border-top"
          >
            <div class="d-flex align-items-center mb-2 mb-sm-0">
              <span class="text-muted font-size-sm mr-3">
                แสดง {{ paginationFrom }} ถึง {{ paginationTo }} จากทั้งหมด {{ totalRows }} รายการ
              </span>
              <b-form-select
                v-model="perPage"
                size="sm"
                :options="perPageOptions"
                style="width: 85px;"
                @change="onPerPageChange"
              />
            </div>
            <b-pagination
              v-model="page"
              :total-rows="totalRows"
              :per-page="perPage"
              class="mb-0"
              size="sm"
              @input="onPageChange"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Adjust Point Modal -->
    <b-modal
      id="modal-adjust-point"
      ref="adjustModalRef"
      size="lg"
      title="ปรับแต้มสมาชิกด้วยตนเอง (Manual Point Adjustment)"
      header-class="py-4 px-6 border-bottom"
      body-class="px-6 py-5"
      footer-class="py-3 px-6 border-top d-flex justify-content-between"
      no-close-on-backdrop
    >
      <div class="border rounded bg-light-primary p-4 mb-5">
        <div class="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <strong class="text-dark font-size-base">{{ memberInfo.name }}</strong>
            <span class="text-muted ml-2">({{ memberInfo.phone }})</span>
          </div>
          <div class="mt-1 mt-sm-0">
            <span class="badge badge-light-primary font-weight-bold px-2 py-1 mr-2">
              แต้มคงเหลือ: {{ Number(summary.balance || 0).toLocaleString() }} แต้ม
            </span>
            <span
              class="badge font-weight-bold px-2 py-1"
              :style="{
                backgroundColor: (summary.current_tier.color || '#3699FF') + '20',
                color: summary.current_tier.color || '#3699FF'
              }"
            >
              {{ summary.current_tier.name }}
            </span>
          </div>
        </div>
      </div>

      <b-form @submit.stop.prevent="submitAdjustment">
        <b-form-group label="ประเภทการปรับแต้ม :" label-class="font-weight-bold text-dark" class="mb-4">
          <div class="d-flex flex-wrap" style="gap: 12px;">
            <b-button
              type="button"
              :variant="adjustForm.action === 'ADD' ? 'success' : 'outline-success'"
              class="px-5 py-2 font-weight-bold"
              @click="adjustForm.action = 'ADD'"
            >
              เพิ่มแต้ม
            </b-button>
            <b-button
              type="button"
              :variant="adjustForm.action === 'DEDUCT' ? 'danger' : 'outline-danger'"
              class="px-5 py-2 font-weight-bold"
              @click="adjustForm.action = 'DEDUCT'"
            >
              ลดแต้ม
            </b-button>
          </div>
        </b-form-group>

        <b-form-group
          label="จำนวนแต้มที่ต้องการปรับ :"
          label-class="font-weight-bold text-dark"
          :invalid-feedback="pointInvalidFeedback"
          :state="isPointValid"
          class="mb-4"
        >
          <b-input-group :append="adjustForm.action === 'ADD' ? '+ แต้ม' : '- แต้ม'">
            <b-form-input
              v-model.number="adjustForm.points"
              type="number"
              min="1"
              step="1"
              placeholder="ระบุจำนวนแต้ม เช่น 50, 100, 500"
              :state="isPointValid"
              class="form-control-solid"
            />
          </b-input-group>
          <small v-if="adjustForm.action === 'DEDUCT'" class="form-text text-muted mt-1">
            * แต้มคงเหลือสูงสุดที่สามารถลดได้คือ {{ Number(summary.balance || 0).toLocaleString() }} แต้ม
          </small>
        </b-form-group>

        <b-form-group class="mb-4">
          <div class="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
            <div>
              <div class="font-weight-bold text-dark font-size-sm">
                นับรวมในแต้มสะสมตลอดชีพ (มีผลต่อการเลื่อน/ปรับระดับสมาชิก)
              </div>
              <div class="text-muted font-size-xs">
                หากเปิดใช้งาน ระบบจะปรับทั้งแต้มคงเหลือและแต้มตลอดชีพ พร้อมคำนวณระดับ Tier ใหม่ให้อัตโนมัติ
              </div>
            </div>
            <b-form-checkbox
              v-model="adjustForm.adjust_lifetime"
              switch
              size="lg"
              class="ml-3"
            />
          </div>
        </b-form-group>

        <b-form-group
          label="สาเหตุการปรับแต้ม :"
          label-class="font-weight-bold text-dark"
          :invalid-feedback="'กรุณาเลือกหรือระบุสาเหตุการปรับแต้ม'"
          :state="isReasonValid"
          class="mb-4"
        >
          <b-form-select
            v-model="adjustForm.reason_preset"
            :options="reasonOptions"
            :state="isReasonValid"
            class="form-control-solid mb-2"
          />
          <b-form-input
            v-if="adjustForm.reason_preset === 'OTHER'"
            v-model="adjustForm.reason_custom"
            placeholder="โปรดระบุสาเหตุการปรับแต้ม..."
            class="form-control-solid"
          />
        </b-form-group>

        <b-form-group label="หมายเหตุภายใน (Remark) :" label-class="font-weight-bold text-dark" class="mb-4">
          <b-form-textarea
            v-model="adjustForm.remark"
            rows="2"
            max-rows="4"
            placeholder="ระบุรายละเอียดเพิ่มเติมสำหรับบันทึกประวัติการตรวจสอบ (Audit Log)..."
            class="form-control-solid"
          />
        </b-form-group>

        <div class="border rounded p-4 bg-light-secondary mb-2">
          <div class="font-weight-bolder text-dark font-size-sm mb-3">
            สรุปผลการปรับแต้ม (Live Preview) :
          </div>
          <b-row>
            <b-col cols="6">
              <span class="text-muted font-size-xs d-block">แต้มคงเหลือ (Point Balance)</span>
              <div class="d-flex align-items-center mt-1">
                <span class="text-dark font-weight-bold mr-2">
                  {{ Number(summary.balance || 0).toLocaleString() }}
                </span>
                <span class="text-muted mr-2">→</span>
                <span
                  class="font-weight-bolder"
                  :class="previewBalance >= 0 ? 'text-primary' : 'text-danger'"
                >
                  {{ Number(previewBalance).toLocaleString() }} แต้ม
                </span>
              </div>
            </b-col>

            <b-col cols="6">
              <span class="text-muted font-size-xs d-block">แต้มสะสมตลอดชีพ (Lifetime Point)</span>
              <div class="d-flex align-items-center mt-1">
                <span class="text-muted font-weight-bold mr-2">
                  {{ Number(summary.lifetime_point || 0).toLocaleString() }}
                </span>
                <span class="text-muted mr-2">→</span>
                <span class="font-weight-bolder text-success">
                  {{ Number(previewLifetime).toLocaleString() }} แต้ม
                </span>
              </div>
            </b-col>
          </b-row>

          <div v-if="previewTierChange" class="mt-3 pt-2 border-top">
            <span class="badge badge-light-warning font-weight-bold px-2 py-1">
              ระดับสมาชิกจะเปลี่ยนเป็น: <strong>{{ previewNewTier.name }}</strong>
            </span>
          </div>
        </div>
      </b-form>

      <template #modal-footer="{ cancel }">
        <b-button variant="light" class="font-weight-bold px-5" @click="cancel()">
          ยกเลิก
        </b-button>
        <b-button
          variant="primary"
          class="font-weight-bold px-6"
          :disabled="!canSubmitAdjustment || isSubmitting"
          @click="submitAdjustment"
        >
          <b-spinner v-if="isSubmitting" small class="mr-1"></b-spinner>
          บันทึกการปรับแต้ม
        </b-button>
      </template>
    </b-modal>

    <!-- Transaction Detail Modal -->
    <b-modal
      id="modal-ledger-detail"
      ref="ledgerDetailModalRef"
      size="md"
      title="รายละเอียดรายการแต้ม (Transaction Detail)"
      header-class="py-4 px-6 border-bottom"
      body-class="px-6 py-5"
      footer-class="py-3 px-6 border-top d-flex justify-content-end"
      no-close-on-backdrop
    >
      <div v-if="selectedItem" class="font-size-sm">
        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">รหัสรายการ (Reference Code)</span>
          <span class="font-weight-bolder text-dark">{{ selectedItem.reference_code || '—' }}</span>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">วันที่ทำรายการ</span>
          <span class="text-dark">{{ formatDateTime(selectedItem.created_at) }}</span>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">ประเภทรายการ</span>
          <span
            class="label label-inline font-weight-bold"
            :class="getTypeBadgeClass(selectedItem.type)"
          >
            {{ getTypeLabel(selectedItem.type) }}
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-start pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold mr-3">แคมเปญ</span>
          <span class="text-dark text-right font-weight-bold" style="max-width: 60%;">
            {{ selectedItem.campaign ? selectedItem.campaign.name : '—' }}
          </span>
        </div>

        <div
          v-if="selectedItem.base_point != null"
          class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3"
        >
          <span class="text-muted font-weight-bold">แต้มที่ได้รับ</span>
          <span class="text-dark font-weight-bold">
            {{ Number(selectedItem.base_point).toLocaleString() }} แต้ม
          </span>
        </div>

        <div
          v-if="selectedItem.point_multiplier != null"
          class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3"
        >
          <span class="text-muted font-weight-bold">สิทธิพิเศษ</span>
          <span class="text-dark font-weight-bold">
            x{{ selectedItem.point_multiplier }}
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">การเปลี่ยนแปลงแต้ม</span>
          <div class="d-flex align-items-center">
            <span
              class="font-weight-bolder font-size-h6"
              :class="selectedItem.earned_point > 0 ? 'text-success' : 'text-danger'"
            >
              {{ selectedItem.earned_point > 0 ? '+' : '' }}{{ Number(selectedItem.earned_point).toLocaleString() }}
            </span>
            <span class="text-muted font-size-xs ml-1">แต้ม</span>
          </div>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">แต้มคงเหลือ (Balance)</span>
          <span class="text-dark font-weight-bold">
            {{ Number(selectedItem.balance_before || 0).toLocaleString() }} → {{ Number(selectedItem.balance_after || 0).toLocaleString() }} แต้ม
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">แต้มสะสมตลอดชีพ (Lifetime)</span>
          <span class="text-muted font-weight-bold">
            {{ Number(selectedItem.lifetime_before || 0).toLocaleString() }} → {{ Number(selectedItem.lifetime_after || 0).toLocaleString() }} แต้ม
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold">ระดับสมาชิก ณ ขณะนั้น</span>
          <span
            v-if="selectedItem.member_tier"
            class="badge font-weight-bold px-2 py-1 font-size-xs"
            :style="{
              backgroundColor: (selectedItem.member_tier.color || '#3699FF') + '20',
              color: selectedItem.member_tier.color || '#3699FF',
              border: `1px solid ${(selectedItem.member_tier.color || '#3699FF')}40`
            }"
          >
            {{ selectedItem.member_tier.name }}
          </span>
          <span v-else class="text-muted">—</span>
        </div>

        <div class="d-flex justify-content-between align-items-start pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold mr-3">รายละเอียด / ที่มา</span>
          <span class="text-dark text-right font-weight-bold" style="max-width: 60%;">
            {{ selectedItem.reason || selectedItem.source_type_label || '—' }}
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-start pb-3 border-bottom mb-3">
          <span class="text-muted font-weight-bold mr-3">หมายเหตุ (Remark)</span>
          <span class="text-muted text-right" style="max-width: 60%;">
            {{ selectedItem.remark || '—' }}
          </span>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <span class="text-muted font-weight-bold">ผู้ทำรายการ</span>
          <span class="text-dark">{{ selectedItem.created_by_name || 'ระบบอัตโนมัติ' }}</span>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="light" class="font-weight-bold px-5" @click="cancel()">
          ปิด
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";

export default {
  name: "CampaignMemberPointLedger",
  props: {
    embed: {
      type: Boolean,
      default: false
    },
    campaignId: {
      type: [String, Number],
      default: null
    },
    customerCampaignId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      loading: false,
      isSubmitting: false,
      selectedItem: null,
      filterType: "ALL",
      filterCampaign: "ALL",
      ledgerCampaigns: [],
      page: 1,
      perPage: 10,
      totalRows: 0,
      perPageOptions: [
        { value: 10, text: "10 รายการ" },
        { value: 25, text: "25 รายการ" },
        { value: 50, text: "50 รายการ" }
      ],
      typeFilterOptions: [
        { value: "ALL", text: "ประเภททั้งหมด" },
        { value: "EARN", text: "ได้รับแต้ม" },
        // { value: "ADJUST", text: "ปรับแต้มมือ" },
        { value: "REDEEM", text: "แลกของรางวัล" },
        { value: "REVERSAL", text: "ยกเลิกแต้ม" },
        { value: "EXPIRE", text: "แต้มหมดอายุ" }
      ],
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "45px" } },
        { key: "created_at", label: "วันที่ทำรายการ", class: "text-center", thClass: "text-nowrap", thStyle: { width: "165px" } },
        { key: "type", label: "ประเภท", class: "text-center", thClass: "text-nowrap", thStyle: { width: "110px" } },
        { key: "campaign", label: "แคมเปญ", class: "text-center", thClass: "text-nowrap", thStyle: { minWidth: "140px" } },
        { key: "base_point", label: "แต้มที่ได้รับ", class: "text-center text-nowrap", thStyle: { width: "120px" } },
        { key: "multiplier", label: "สิทธิพิเศษ", class: "text-center text-nowrap", thStyle: { width: "90px" } },
        { key: "points", label: "การเปลี่ยนแปลงแต้ม", class: "text-center text-nowrap", thStyle: { width: "140px" } },
        { key: "balance_after", label: "คงเหลือ", class: "text-center text-nowrap", thStyle: { width: "110px" } },
        { key: "lifetime_after", label: "ตลอดชีพ", class: "text-center text-nowrap", thStyle: { width: "110px" } },
        { key: "tier", label: "ระดับสมาชิก", class: "text-center text-nowrap", thStyle: { width: "110px" } },
        { key: "created_by", label: "ผู้ทำรายการ", class: "text-center", thClass: "text-nowrap", thStyle: { width: "140px" } },
        { key: "actions", label: "", class: "text-center", thStyle: { width: "60px" } }
      ],
      summary: {
        balance: 0,
        lifetime_point: 0,
        current_tier: {
          id: null,
          name: "สมาชิกทั่วไป",
          color: "#9E9E9E",
          multiplier: 1,
          min_point: 0
        },
        tiers: []
      },
      memberInfo: {
        name: "—",
        phone: "—"
      },
      items: [],
      adjustForm: {
        action: "ADD",
        points: null,
        adjust_lifetime: true,
        reason_preset: "ชดเชยข้อผิดพลาดของระบบ / ใบเสร็จ",
        reason_custom: "",
        remark: ""
      },
      reasonOptions: [
        { value: "ชดเชยข้อผิดพลาดของระบบ / ใบเสร็จ", text: "ชดเชยข้อผิดพลาดของระบบ / ใบเสร็จ" },
        { value: "กิจกรรมส่งเสริมการขาย / โปรโมชั่นพิเศษ", text: "กิจกรรมส่งเสริมการขาย / โปรโมชั่นพิเศษ" },
        { value: "ปรับปรุงยอดแต้มให้ถูกต้อง", text: "ปรับปรุงยอดแต้มให้ถูกต้อง" },
        { value: "ตัดแต้มเนื่องจากผิดเงื่อนไขการใช้งาน", text: "ตัดแต้มเนื่องจากผิดเงื่อนไขการใช้งาน" },
        { value: "OTHER", text: "อื่นๆ (ระบุเอง)" }
      ]
    };
  },
  computed: {
    resolvedCampaignId() {
      if (this.embed && this.campaignId != null && this.campaignId !== "") {
        return this.campaignId;
      }
      return this.$route.params.campaignId;
    },
    resolvedCustomerCampaignId() {
      if (
        this.embed &&
        this.customerCampaignId != null &&
        this.customerCampaignId !== ""
      ) {
        return this.customerCampaignId;
      }
      return this.$route.params.customerCampaignId;
    },
    campaignFilterOptions() {
      return [
        { value: "ALL", text: "ทุกแคมเปญ" },
        ...this.ledgerCampaigns.map(c => ({ value: c.id, text: c.name }))
      ];
    },
    paginationFrom() {
      if (this.totalRows === 0) return 0;
      return (this.page - 1) * this.perPage + 1;
    },
    paginationTo() {
      const to = this.page * this.perPage;
      return to > this.totalRows ? this.totalRows : to;
    },
    isPointValid() {
      if (this.adjustForm.points === null || this.adjustForm.points === "") return null;
      const p = Number(this.adjustForm.points);
      if (isNaN(p) || p <= 0 || !Number.isInteger(p)) return false;
      if (this.adjustForm.action === "DEDUCT" && p > this.summary.balance) return false;
      return true;
    },
    pointInvalidFeedback() {
      const p = Number(this.adjustForm.points);
      if (isNaN(p) || p <= 0) return "กรุณาระบุจำนวนแต้มที่มากกว่า 0";
      if (!Number.isInteger(p)) return "จำนวนแต้มต้องเป็นจำนวนเต็มเท่านั้น";
      if (this.adjustForm.action === "DEDUCT" && p > this.summary.balance) {
        return `ไม่สามารถลดแต้มเกินแต้มคงเหลือปัจจุบัน (${this.summary.balance} แต้ม)`;
      }
      return "";
    },
    isReasonValid() {
      if (this.adjustForm.reason_preset === "OTHER") {
        return Boolean(this.adjustForm.reason_custom && this.adjustForm.reason_custom.trim());
      }
      return Boolean(this.adjustForm.reason_preset);
    },
    canSubmitAdjustment() {
      return this.isPointValid === true && this.isReasonValid;
    },
    previewDelta() {
      const p = Number(this.adjustForm.points) || 0;
      return this.adjustForm.action === "ADD" ? p : -p;
    },
    previewBalance() {
      return Math.max(0, this.summary.balance + this.previewDelta);
    },
    previewLifetime() {
      if (!this.adjustForm.adjust_lifetime) {
        return this.summary.lifetime_point;
      }
      return Math.max(0, this.summary.lifetime_point + this.previewDelta);
    },
    previewNewTier() {
      const lt = this.previewLifetime;
      const tiers = (this.summary && this.summary.tiers) ? this.summary.tiers : [];
      if (!tiers.length) {
        return this.summary && this.summary.current_tier ? this.summary.current_tier : { name: "สมาชิกทั่วไป", color: "#9E9E9E" };
      }
      const matched = [...tiers].reverse().find(t => lt >= (Number(t.min_point) || 0));
      return matched || tiers[0] || this.summary.current_tier;
    },
    previewTierChange() {
      if (!this.previewNewTier || !this.summary.current_tier) return false;
      return this.previewNewTier.name !== this.summary.current_tier.name;
    }
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    formatDateTime(v) {
      if (!v) return "—";
      try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return v;
        return d.toLocaleString("th-TH");
      } catch (e) {
        return v;
      }
    },
    getTypeLabel(type) {
      const map = {
        EARN: "ได้รับแต้ม",
        ADJUST: "ปรับแต้มมือ",
        REDEEM: "แลกรางวัล",
        REVERSAL: "ยกเลิกแต้ม",
        EXPIRE: "แต้มหมดอายุ"
      };
      return map[type] || type;
    },
    getTypeBadgeClass(type) {
      const map = {
        EARN: "label-light-success",
        ADJUST: "label-light-primary",
        REDEEM: "label-light-warning",
        REVERSAL: "label-light-danger",
        EXPIRE: "label-light"
      };
      return map[type] || "label-light";
    },
    onFilterChange() {
      this.page = 1;
      this.fetchData();
    },
    onPerPageChange() {
      this.page = 1;
      this.fetchData();
    },
    onPageChange(p) {
      this.page = p;
      this.fetchData();
    },
    openAdjustModal() {
      this.adjustForm = {
        action: "ADD",
        points: null,
        adjust_lifetime: true,
        reason_preset: "ชดเชยข้อผิดพลาดของระบบ / ใบเสร็จ",
        reason_custom: "",
        remark: ""
      };
      this.$bvModal.show("modal-adjust-point");
    },
    openDetailModal(item) {
      this.selectedItem = item;
      this.$bvModal.show("modal-ledger-detail");
    },
    async fetchData() {
      if (!this.resolvedCampaignId || !this.resolvedCustomerCampaignId) {
        return;
      }

      this.loading = true;
      try {
        ApiService.setHeader();
        const { data } = await ApiService.getParams(
          `campaign/${this.resolvedCampaignId}/members/${this.resolvedCustomerCampaignId}/point_ledger`,
          {
            params: {
              page: String(this.page),
              perPage: String(this.perPage),
              type: this.filterType,
              campaign_filter: String(this.filterCampaign)
            }
          }
        );

        if (data && data.status === "success" && data.data) {
          const res = data.data;
          this.items = Array.isArray(res.data) ? res.data : [];
          this.totalRows = Number(res.total) || 0;
          this.ledgerCampaigns = Array.isArray(res.campaigns) ? res.campaigns : [];
          if (res.summary) {
            this.summary = {
              balance: Number(res.summary.balance) || 0,
              lifetime_point: Number(res.summary.lifetime_point) || 0,
              current_tier: res.summary.current_tier || this.summary.current_tier,
              tiers: Array.isArray(res.summary.tiers) ? res.summary.tiers : []
            };
          }
          if (res.member_info) {
            this.memberInfo = {
              name: res.member_info.name || "—",
              phone: res.member_info.phone || "—"
            };
          }
        } else {
          this.items = [];
          this.totalRows = 0;
        }
      } catch (error) {
        this.items = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },
    async submitAdjustment() {
      if (!this.canSubmitAdjustment) return;

      const reasonText =
        this.adjustForm.reason_preset === "OTHER"
          ? this.adjustForm.reason_custom.trim()
          : this.adjustForm.reason_preset;

      const actionText = this.adjustForm.action === "ADD" ? "เพิ่มแต้ม" : "ลดแต้ม";
      const sign = this.adjustForm.action === "ADD" ? "+" : "-";
      const points = Number(this.adjustForm.points);

      const escapeHtml = (str) => {
        if (!str) return "";
        return String(str)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      };

      const confirmResult = await Swal.fire({
        title: `ยืนยันการ${actionText} ?`,
        html: `
          <div class="text-left font-size-sm">
            <p class="mb-1"><strong>สมาชิก:</strong> ${escapeHtml(this.memberInfo.name)}</p>
            <p class="mb-1"><strong>จำนวน:</strong> <span class="${this.adjustForm.action === 'ADD' ? 'text-success' : 'text-danger'} font-weight-bolder font-size-h5">${sign}${points.toLocaleString()}</span> แต้ม</p>
            <p class="mb-1"><strong>สาเหตุ:</strong> ${escapeHtml(reasonText)}</p>
            <p class="mb-0"><strong>แต้มคงเหลือใหม่:</strong> ${this.previewBalance.toLocaleString()} แต้ม</p>
          </div>
        `,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "ยืนยันการปรับแต้ม",
        cancelButtonText: "ยกเลิก",
        customClass: {
          confirmButton: "btn btn-primary font-weight-bold px-6",
          cancelButton: "btn btn-light font-weight-bold px-6"
        }
      });

      if (!confirmResult.isConfirmed) return;

      this.isSubmitting = true;
      try {
        ApiService.setHeader();
        const payload = {
          action: this.adjustForm.action,
          points: points,
          adjust_lifetime: this.adjustForm.adjust_lifetime,
          reason: reasonText,
          remark: this.adjustForm.remark || null
        };

        const { data } = await ApiService.post(
          `campaign/${this.resolvedCampaignId}/members/${this.resolvedCustomerCampaignId}/adjust_point`,
          payload
        );

        if (data && data.status === "success") {
          this.$bvModal.hide("modal-adjust-point");
          await this.fetchData();

          Swal.fire({
            title: "ปรับแต้มสำเร็จ!",
            text: `ทำการ${actionText} ${points.toLocaleString()} แต้ม ให้คุณ${this.memberInfo.name} เรียบร้อยแล้ว`,
            icon: "success",
            confirmButtonText: "ตกลง",
            customClass: {
              confirmButton: "btn btn-primary font-weight-bold px-6"
            }
          });
        } else {
          const errMsg =
            (data && data.error && (data.error.message || data.error)) ||
            (data && data.message) ||
            "เกิดข้อผิดพลาดในการปรับแต้ม";
          Swal.fire({
            title: "ไม่สามารถปรับแต้มได้",
            text: errMsg,
            icon: "error",
            confirmButtonText: "ตกลง"
          });
        }
      } catch (error) {
        const resData = error.response && error.response.data;
        const errMsg =
          (resData && resData.error && (resData.error.message || resData.error)) ||
          (resData && resData.message) ||
          error.message ||
          "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์";
        Swal.fire({
          title: "เกิดข้อผิดพลาด",
          text: errMsg,
          icon: "error",
          confirmButtonText: "ตกลง"
        });
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.campaign-member-point-ledger .table th {
  border-top: none;
  font-weight: 600;
  color: #464e5f;
  background-color: #f3f6f9;
}
.campaign-member-point-ledger .table td {
  vertical-align: middle;
}
.campaign-member-point-ledger .ledger-table tbody tr {
  cursor: pointer;
}
.campaign-member-point-ledger .created-by-name {
  max-width: 130px;
  vertical-align: middle;
}
</style>
